import { useMemo } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import {
  type ExternalMonitoringLanguageAggregation,
  type ExternalMonitoringLanguageAggregationItem,
} from '../language-aggregation';

/** Первые четыре сегмента — прежняя палитра дашборда; дальше — стабильный HSL по коду языка */
const BASE_PIE_COLORS = ['#28A745', '#17a2b8', '#FFC107', '#DC3545'] as const;

function hashLanguageKey(languageCode: string, languageId: number): number {
  const s = `${languageCode}\0${languageId}`;
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash << 5) - hash + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function hslColorForExtraLanguage(item: ExternalMonitoringLanguageAggregationItem): string {
  const h = hashLanguageKey(item.languageCode, item.languageId) % 360;
  const h2 = hashLanguageKey(item.languageCode, item.languageId);
  const s = 52 + (h2 % 24);
  const l = 44 + ((h2 >> 6) % 10);
  return `hsl(${h} ${s}% ${l}%)`;
}

function pieColorsForItems(items: ExternalMonitoringLanguageAggregationItem[]): string[] {
  return items.map((item, index) =>
    index < BASE_PIE_COLORS.length ? BASE_PIE_COLORS[index] : hslColorForExtraLanguage(item),
  );
}

export const useCommentsByLanguagePie = (
  _period?: ExternalMonitoringPeriodBounds,
  aggregation?: ExternalMonitoringLanguageAggregation | null,
): ChartProps => {
  const chartData = useMemo<ReactApexChartProps>(() => {
    const items = aggregation?.items ?? [];
    const languages = items.map((x) => x.languageLabel);
    const counts = items.map((x) => x.commentsCount);
    return {
      series: counts,
      options: {
        chart: {
          type: 'pie',
          toolbar: { show: false },
        },
        labels: languages,
        colors: pieColorsForItems(items),
        legend: { position: 'right' },
        dataLabels: { enabled: true },
      },
    };
  }, [aggregation, _period?.begin, _period?.end]);

  return {
    ...chartData,
    label: 'Языки комментариев',
    labelTooltip:
      'Отображает процентное распределение отзывов по языкам написания и использование эмодзи. Позволяет оценить лингвистический состав аудитории. Всплывающая подсказка при наведении на сегмент диаграммы показывает точное количество комментариев на выбранном языке.',
    labelInfoIcon: true,
    type: 'pie',
  };
};
